"use client"

import { useState, useEffect, useCallback } from "react"
import { useQueryClient } from "@tanstack/react-query"
import api from "../../../services/axios"
import Button from "../../button-v1"
import CollapsibleSection from "../../collapsableSection"
import styles from "../../../styles/inventory/MoveSupplyForm.module.css"
import DatePicker from "../../shared-modules/DatePicker/DatePicker"
import Modal from "../modules/Modal/Moadal"
import Select from "../modules/Select/Select"
import Input from "../modules/Input/Input"
import { getLocationList } from "../../../services/inventory/detail"

import { moveDevicesToTenant, moveBoxesToTenant } from "../../../services/inventory/movement"

import toast from "react-hot-toast"

const getAccountOptionsDefault = () => [
  { value: "rebul-consultant", label: "Rebul Consultant" },
  { value: "healthcare-partners", label: "Healthcare Partners" },
  { value: "medical-solutions", label: "Medical Solutions" },
  { value: "wellness-group", label: "Wellness Group" },
]

const safeJSONParse = (s) => {
  try {
    return JSON.parse(s)
  } catch {
    return null
  }
}

const initializeCollapsedState = (items) => {
  const collapsed = {}
  items.forEach((item) => {
    collapsed[item.id] = true
  })
  return collapsed
}

const formatServerError = (data) => {
  if (!data && data !== "") return "Unknown error"
  if (typeof data === "string") {
    const parsed = safeJSONParse(data)
    if (parsed) return JSON.stringify(parsed)
    return data
  }
  if (typeof data === "object") {
    if (data.error) return typeof data.error === "string" ? data.error : JSON.stringify(data.error)
    if (data.message) return typeof data.message === "string" ? data.message : JSON.stringify(data.message)
    try {
      return JSON.stringify(data)
    } catch {
      return String(data)
    }
  }
  return String(data)
}

const initialFormData = {
  caseManager: "",
  moveFromLocation: "",
  moveFromLocationId: null,
  moveToAccount: "",
  moveToLocation: "",
  moveDate: "",
  deliveredBy: "", // NEW: delivered by field
}

const MoveDeviceForm = ({ devices = [], isBulkMove = false, isOpen, onClose, onSuccess }) => {
  const queryClient = useQueryClient()

  const [selectedDevices, setSelectedDevices] = useState(devices)
  const [formData, setFormData] = useState({ ...initialFormData })
  const [collapsedItems, setCollapsedItems] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)

  const [selectedDate, setSelectedDate] = useState(() => new Date())
  const [selectedOrderNumber, setSelectedOrderNumber] = useState("")

  const [accounts, setAccounts] = useState([])
  const [locations, setLocations] = useState([])
  const [orders, setOrders] = useState([])

  const [loadingAccounts, setLoadingAccounts] = useState(false)
  const [loadingLocations, setLoadingLocations] = useState(false)
  const [loadingOrders, setLoadingOrders] = useState(false)
  const [orderDetailLoading, setOrderDetailLoading] = useState(false)
  const [accountDisabled, setAccountDisabled] = useState(false)
  const [locationDisabled, setLocationDisabled] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const resetAll = useCallback(() => {
    setSelectedDevices([])
    setFormData({ ...initialFormData })
    setCollapsedItems({})
    setIsFormValid(false)
    setSelectedDate(new Date())
    setSelectedOrderNumber("")
    setAccounts([])
    setLocations([])
    setOrders([])
    setLoadingAccounts(false)
    setLoadingLocations(false)
    setLoadingOrders(false)
    setOrderDetailLoading(false)
    setAccountDisabled(false)
    setLocationDisabled(false)
    setSubmitting(false)
  }, [])

  useEffect(() => {
    if (devices && devices.length > 0) {
      setSelectedDevices(devices)
      setFormData((prev) => ({
        ...prev,
        caseManager: devices[0]?.caseManager || prev.caseManager || "",
        moveFromLocation: devices[0]?.location || prev.moveFromLocation || "",
        moveFromLocationId: devices[0]?.location_id || prev.moveFromLocationId || null,
      }))
      setCollapsedItems(initializeCollapsedState(devices))
    } else {
      setSelectedDevices([])
      setCollapsedItems({})
    }
  }, [devices])

  useEffect(() => {
    if (isOpen) {
      const user = safeJSONParse(typeof window !== "undefined" ? localStorage.getItem("user") : null)
      const currentLoc = safeJSONParse(typeof window !== "undefined" ? sessionStorage.getItem("current_loc") : null)

      setSelectedDate((sd) => sd || new Date())

      setFormData((prev) => ({
        ...prev,
        caseManager: prev.caseManager || user?.name || user?.email || "",
        moveFromLocation: prev.moveFromLocation || currentLoc?.name || "",
        moveFromLocationId: prev.moveFromLocationId || currentLoc?.id || null,
        moveDate:
          prev.moveDate ||
          (selectedDate ? selectedDate.toISOString().split("T")[0] : new Date().toISOString().split("T")[0]),
        // Prefill deliveredBy with user name/email if available (optional)
        deliveredBy: prev.deliveredBy || user?.name || user?.email || "",
      }))

      fetchAccounts()
      fetchPendingOrders()
    } else {
      resetAll()
    }

  }, [isOpen])

  useEffect(() => {
    const valid =
      !!formData.caseManager && !!formData.moveToLocation && !!formData.moveDate && selectedDevices.length > 0
    setIsFormValid(valid)
  }, [formData, selectedDevices])

  const fetchAccounts = async () => {
    setLoadingAccounts(true)
    try {
      const res = await api.get(`/tenants/`)
      const accountsData = Array.isArray(res.data) ? res.data : res.data?.results || res.data || []
      setAccounts(accountsData)
    } catch (err) {
      console.error("Error fetching tenants (/tenants/):", err?.message || err)
      const payload = err?.response?.data ?? err?.message ?? "Failed to load accounts"
      toast.error(formatServerError(payload))
      setAccounts([])
    } finally {
      setLoadingAccounts(false)
    }
  }

  const fetchLocationsByAccount = async (accountId) => {
    if (!accountId) {
      setLocations([])
      return []
    }

    setLoadingLocations(true)
    try {
      const res = await api.get(`/accounts/${accountId}/locations/?status=active`)

      const locsRaw = res?.data?.locations || res?.data || []
      const locs = Array.isArray(locsRaw)
        ? locsRaw.map((l) => ({ id: l.id, name: l.name || l.location_name || String(l.id), raw: l }))
        : []
      setLocations(locs)
      return locs
    } catch (err) {
      console.error("Error fetching account locations:", err?.message || err)
      const payload = err?.response?.data ?? err?.message ?? "Failed to load locations"
      toast.error(formatServerError(payload))

      try {
        const fallback = await getLocationList()
        if (fallback && fallback.success && Array.isArray(fallback.data)) {

          const filtered = fallback.data.filter((l) => String(l.account_id || l.tenant_id) === String(accountId))
          const norm = filtered.map((l) => ({ id: l.id, name: l.name || l.location_name || String(l.id), raw: l }))
          setLocations(norm)
          return norm
        }
      } catch (e) {
        console.error("Fallback getLocationList failed:", e?.message || e)
      }
      setLocations([])
      return []
    } finally {
      setLoadingLocations(false)
    }
  }

  const fetchPendingOrders = async () => {
    setLoadingOrders(true)
    try {
      const res = await api.get(`/inventory/toca/client-bulk-orders/?status=pending`)
      let list = []
      if (Array.isArray(res.data)) list = res.data
      else if (res.data?.orders) list = res.data.orders
      else if (res.data?.results) list = res.data.results
      else if (res.data?.data) list = res.data.data
      list = list.filter((o) => (o.status || "").toLowerCase() === "pending")
      setOrders(list)
    } catch (err) {
      console.error("fetchPendingOrders error (status=pending):", err?.message || err)
      const payload = err?.response?.data ?? err?.message ?? "Failed to load orders"
      toast.error(formatServerError(payload))
      try {
        const res2 = await api.get(`/inventory/toca/client-bulk-orders/`)
        let list = res2.data?.orders || res2.data?.results || []
        list = list.filter((o) => (o.status || "").toLowerCase() === "pending")
        setOrders(list)
      } catch (e) {
        console.error("fallback fetch all orders failed:", e?.message || e)
        setOrders([])
      }
    } finally {
      setLoadingOrders(false)
    }
  }

  const fetchOrderDetailByOrderNumber = async (orderNumber) => {
    if (!orderNumber) return null
    setOrderDetailLoading(true)
    try {
      const res = await api.get(
        `/inventory/toca/client-bulk-orders/?order_number=${encodeURIComponent(orderNumber)}`,
      )
      const ordersArr = res?.data?.orders || res?.data?.results || []
      if (!ordersArr || ordersArr.length === 0) return null
      return ordersArr[0]
    } catch (err) {
      console.error("fetchOrderDetailByOrderNumber error:", err?.message || err)
      const payload = err?.response?.data ?? err?.message ?? "Failed to fetch order details"
      toast.error(formatServerError(payload))
      return null
    } finally {
      setOrderDetailLoading(false)
    }
  }

  const handleInputChange = (name, value) => {
    if (name === "moveToAccount") {

      const previousLocation = formData.moveToLocation
      setFormData((prev) => ({ ...prev, moveToAccount: value, moveToLocation: "" }))

      fetchLocationsByAccount(value).then((locs) => {
        if (previousLocation && locs.some((l) => String(l.id) === String(previousLocation))) {
          setFormData((prev) => ({ ...prev, moveToLocation: previousLocation }))
        }
      })
      return
    }

    if (name === "moveToLocation") {

      setFormData((prev) => ({ ...prev, moveToLocation: value }))

      const foundInCurrent = (locations || []).find((l) => String(l.id) === String(value))
      if (foundInCurrent) {
        return
      }

      ;(async () => {
        try {
          const fallback = await getLocationList()
          if (fallback && Array.isArray(fallback)) {
            const found = fallback.find((l) => String(l.id) === String(value))
            if (found && (found.account_id || found.tenant_id)) {
              const ownerId = found.account_id || found.tenant_id
              setFormData((prev) => ({ ...prev, moveToAccount: ownerId }))

              const norm = fallback
                .filter((l) => String(l.account_id || l.tenant_id) === String(ownerId))
                .map((l) => ({ id: l.id, name: l.name || l.location_name || String(l.id), raw: l }))
              setLocations(norm)
            }
          }
        } catch (e) {

        }
      })()

      return
    }

    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleOrderSelect = async (orderNumber) => {
    setSelectedOrderNumber(orderNumber)

    if (!orderNumber) {

      setAccountDisabled(false)
      setLocationDisabled(false)
      setFormData((prev) => ({ ...prev, moveToAccount: "", moveToLocation: "" }))

      setLocations([])
      return
    }

    const order = await fetchOrderDetailByOrderNumber(orderNumber)
    if (!order) {
      console.warn(`Order details not found for order_number=${orderNumber}`)
      toast.error("Order not found")
      return
    }

    const tenantId = order?.tenant
    const orderLocationId = order?.location_id
    const orderLocationName = order?.location_name

    if (tenantId) {

      const locs = await fetchLocationsByAccount(tenantId)

      let matchedLoc = (locs || []).find((l) => String(l.id) === String(orderLocationId))

      if (!matchedLoc && orderLocationId) {
        matchedLoc = { id: orderLocationId, name: orderLocationName || `Location ${orderLocationId}` }
        setLocations((prev) => {
          if (!prev.some((p) => String(p.id) === String(matchedLoc.id))) return [...prev, matchedLoc]
          return prev
        })
      }

      setFormData((prev) => ({
        ...prev,
        moveToAccount: tenantId,
        moveToLocation: matchedLoc ? matchedLoc.id : orderLocationId,
      }))

      setAccountDisabled(true)
      setLocationDisabled(true)
    } else {

      if (orderLocationId) {
        setFormData((prev) => ({ ...prev, moveToLocation: orderLocationId }))
        setLocationDisabled(true)
      }
    }
  }

  const handleRemoveDevice = (deviceId) => {
    setSelectedDevices((prev) => prev.filter((device) => device.id !== deviceId))
    setCollapsedItems((prev) => {
      const copy = { ...prev }
      delete copy[deviceId]
      return copy
    })
  }

  const updateIndividualDevicesCache = useCallback(
    (movedDeviceIds = [], tenantId, locationId, locationName) => {
      if (!Array.isArray(movedDeviceIds) || movedDeviceIds.length === 0) return
      try {
        queryClient.setQueryData(["individualDevices"], (old = []) => {
          if (!Array.isArray(old)) return old
          return old.map((item) => {
            if (movedDeviceIds.some((mid) => String(mid) === String(item.id))) {
              return {
                ...item,
                tenant_id: tenantId ?? item.tenant_id,
                current_location_name: locationName ?? item.current_location_name,
                current_location_id: locationId ?? item.current_location_id ?? item.location_id,
              }
            }
            return item
          })
        })
      } catch (err) {
        console.error("Error updating individual devices cache:", err?.message || err)
      }
    },
    [queryClient],
  )

  const updateWarehouseBoxesCache = useCallback(
    (movedBoxIds = [], tenantId, locationId, locationName) => {
      if (!Array.isArray(movedBoxIds) || movedBoxIds.length === 0) return
      try {
        queryClient.setQueryData(["warehouseBoxes"], (old = []) => {
          if (!Array.isArray(old)) return old
          return old.map((item) => {
            if (movedBoxIds.some((bid) => String(bid) === String(item.id))) {
              return {
                ...item,
                tenant_id: tenantId ?? item.tenant_id,
                current_location_name: locationName ?? item.current_location_name,
                current_location_id: locationId ?? item.current_location_id ?? item.location_id,
              }
            }
            return item
          })
        })
      } catch (err) {
        console.error("Error updating warehouse boxes cache:", err?.message || err)
      }
    },
    [queryClient],
  )

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isFormValid || submitting) return

    setSubmitting(true)

    const tenantId = formData.moveToAccount
    const locationId = formData.moveToLocation
    const locationName =
      (locations.find((l) => String(l.id) === String(locationId)) || {}).name || formData.moveToLocation

    const first = selectedDevices[0] || {}
    const treatAsBoxes = isBulkMove || !first?.serialImei

    const movedAt = selectedDate ? selectedDate.toISOString() : new Date().toISOString()
    const fromLocation = formData.moveFromLocation || ""
    const deliveredBy = formData.deliveredBy || "" // NEW included in payload

    try {
      let response = null

      if (!treatAsBoxes) {
        const deviceIds = selectedDevices.map((d) => d.originalDevice?.id || d.id).filter(Boolean)
        const payload = {
          tenant_id: tenantId,
          device_ids: deviceIds,
          location_id: Number(locationId),
          moved_at: movedAt,
          from_location: fromLocation,
          delivered_by: deliveredBy, // NEW
        }
        if (selectedOrderNumber) payload.order_number = selectedOrderNumber

        try {
          if (typeof moveDevicesToTenant === "function") {
            response = await moveDevicesToTenant(payload)
          } else {
            response = await api.post(`/inventory/warehouse/devices/move-to-tenant/`, payload)
          }
        } catch (err) {
          console.error("Error calling moveDevicesToTenant:", err?.message || err)
          throw err
        }

        const respData = response?.data ?? response
        const success =
          (!!response && (response.status === 200 || response.status === 201)) ||
          respData?.success === true ||
          respData?.status === 200 ||
          respData?.status === "success"

        if (success) {
          // optimistic cache patch for immediate UI feedback
          updateIndividualDevicesCache(deviceIds, tenantId, Number(locationId), locationName)

          // ensure all relevant query caches are refreshed so tables update correctly
          try {
            queryClient.invalidateQueries(["individualDevices"], { exact: false })
            queryClient.invalidateQueries(["warehouseBoxes"], { exact: false })
            queryClient.invalidateQueries(["deviceTypes"], { exact: false })
          } catch (e) {
            console.warn("Error invalidating queries after devices move:", e?.message || e)
          }

          const successMsg = respData?.message ?? respData?.success_message ?? `Move successful (${deviceIds.length})`
          toast.success(formatServerError(successMsg))

          // call parent onSuccess to allow additional refresh
          if (typeof onSuccess === "function") {
            try {
              await onSuccess()
            } catch (e) {
              console.warn("onSuccess callback failed:", e?.message || e)
            }
          }

          // clear selection locally so modal shows updated state
          setSelectedDevices([])

          handleClose()
        } else {
          const serverPayload =
            respData?.error ?? respData?.errors ?? respData?.message ?? respData ?? response?.response?.data ?? null
          toast.error(formatServerError(serverPayload))
        }
      } else {
        const boxIds = selectedDevices.map((d) => d.originalBox?.id || d.id).filter(Boolean)
        const payload = {
          tenant_id: tenantId,
          box_ids: boxIds,
          location_id: Number(locationId),
          moved_at: movedAt,
          from_location: fromLocation,
          delivered_by: deliveredBy, // NEW
        }
        if (selectedOrderNumber) payload.order_number = selectedOrderNumber

        try {
          if (typeof moveBoxesToTenant === "function") {
            response = await moveBoxesToTenant(payload)
          } else {
            try {
              response = await api.post(`/inventory/warehouse/boxes/move-to-tenant/`, payload)
            } catch (err) {
              console.warn("boxes move endpoint failed, trying device move endpoint as fallback", err?.message || err)
              response = await api.post(`/inventory/warehouse/devices/move-to-tenant/`, payload)
            }
          }
        } catch (err) {
          console.error("Error calling moveBoxesToTenant:", err?.message || err)
          throw err
        }

        const respData = response?.data ?? response
        const success =
          (!!response && (response.status === 200 || response.status === 201)) ||
          respData?.success === true ||
          respData?.status === 200 ||
          respData?.status === "success"

        if (success) {
          // optimistic cache patch for immediate UI feedback
          updateWarehouseBoxesCache(boxIds, tenantId, Number(locationId), locationName)

          // invalidate queries to force fresh data from server (ensures paginated lists update)
          try {
            queryClient.invalidateQueries(["warehouseBoxes"], { exact: false })
            queryClient.invalidateQueries(["individualDevices"], { exact: false })
            queryClient.invalidateQueries(["deviceTypes"], { exact: false })
          } catch (e) {
            console.warn("Error invalidating queries after boxes move:", e?.message || e)
          }

          const successMsg = respData?.message ?? respData?.success_message ?? `Move successful (${boxIds.length})`
          toast.success(formatServerError(successMsg))

          if (typeof onSuccess === "function") {
            try {
              await onSuccess()
            } catch (e) {
              console.warn("onSuccess callback failed:", e?.message || e)
            }
          }

          setSelectedDevices([])

          handleClose()
        } else {
          const serverPayload =
            respData?.error ?? respData?.errors ?? respData?.message ?? respData ?? response?.response?.data ?? null
          toast.error(formatServerError(serverPayload))
        }
      }
    } catch (err) {
      console.error("Move request failed:", err?.message || err)
      const serverPayload = err?.response?.data ?? err?.message ?? "Movement failed."
      toast.error(formatServerError(serverPayload))
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    try {
      resetAll()
    } catch (err) {
      console.error("Error resetting MoveDeviceForm state:", err)
    } finally {
      if (typeof onClose === "function") onClose()
    }
  }

  if (!selectedDevices || selectedDevices.length === 0) {
    return (
      <Modal title="Move Devices" isOpen={isOpen} onClose={handleClose} width="900px" height="85vh">
        <div className={styles.move_device_form} data-move-device-form>
          <div className={styles.empty_state}>
            <p>No devices selected for moving.</p>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  const firstIsIndividual = !!selectedDevices[0]?.serialImei
  const modalTitle = firstIsIndividual ? "Move Device" : "Move Boxes"

  const accountOptions = accounts.map((acc) => ({ value: acc.id, label: acc.name }))
  if (accountOptions.length === 0) {
    getAccountOptionsDefault().forEach((def) => accountOptions.push(def))
  }

  const locationOptions = (locations || []).map((loc) => ({
    value: loc.id,
    label: loc.name || loc.display_name || loc.location_name || String(loc.id),
  }))

  const orderOptions = [
    { value: "", label: "None" },
    ...(orders || []).map((o) => ({
      value: o.order_number || o.orderNumber || o.order_number,
      label: `${o.order_number || o.orderNumber || ""} ${o.tenant_name ? `— ${o.tenant_name}` : ""}`,
    })),
  ]

  return (
    <Modal
      title={modalTitle}
      isOpen={isOpen}
      onClose={handleClose}
      width="800px"
      height="600px"
      footer={
        <div className={styles.button_container}>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="button" variant="primary" disabled={!isFormValid || submitting} onClick={handleSubmit}>
            {submitting
              ? "Moving..."
              : `Move ${selectedDevices.length} ${firstIsIndividual ? "Device" : "Box"}${
                  selectedDevices.length !== 1 ? "s" : ""
                }`}
          </Button>
        </div>
      }
    >
      <div className={styles.move_device_form} data-move-device-form>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.location_row}>
            <div className={styles.form_group}>
              <Input
                label="Case Manager"
                type="text"
                name="caseManager"
                value={formData.caseManager}
                onChange={(e) => handleInputChange("caseManager", e.target.value)}
                placeholder="Dan Oswald"
                required
                fullWidth
              />
            </div>

            <div className={styles.form_group}>
              <label className={styles.label}>Move Date</label>
              <DatePicker
                selectedDate={selectedDate}
                onDateSelect={(date) => {
                  setSelectedDate(date)
                  handleInputChange("moveDate", date.toISOString().split("T")[0])
                }}
                placeholder="Select move date"
              />
            </div>
          </div>

          

          <div className={styles.location_row}>
            <div className={styles.form_group}>
              <Input
                label="Move from Location"
                type="text"
                name="moveFromLocation"
                value={formData.moveFromLocation}
                placeholder="Granada"
                required
                fullWidth
                readOnly
              />
            </div>

            <div className={styles.form_group}>
              <label className={styles.label}>Orders (Optional)</label>
              <Select
                label="Orders"
                value={selectedOrderNumber}
                onChange={(val) => handleOrderSelect(val)}
                options={orderOptions}
                placeholder={loadingOrders ? "Loading orders..." : "Select Order ID"}
                width="100%"
                disabled={loadingOrders || orderDetailLoading}
              />
            </div>
          </div>

          <div className={styles.location_row}>
            <div className={styles.form_group}>
              <label className={styles.label}>Move to Account</label>
              <Select
                label="Move to Account"
                value={formData.moveToAccount}
                onChange={(value) => handleInputChange("moveToAccount", value)}
                options={accountOptions}
                placeholder={loadingAccounts ? "Loading accounts..." : "Select Account"}
                width="100%"
                required
                disabled={loadingAccounts || accountDisabled || orderDetailLoading}
              />
            </div>

            <div className={styles.form_group}>
              <label className={styles.label}>Move to Location</label>
              <Select
                label="Move to Location"
                value={formData.moveToLocation}
                onChange={(value) => handleInputChange("moveToLocation", value)}
                options={locationOptions}
                placeholder={
                  loadingLocations
                    ? "Loading locations..."
                    : !formData.moveToAccount
                    ? "Select account first"
                    : "Select Destination Location"
                }
                width="100%"
                required
                disabled={locationDisabled || loadingLocations || !formData.moveToAccount || orderDetailLoading}
              />
            </div>
            {/* NEW: Delivered By input */}
         
            <div className={styles.form_group} style={{ flex: 1 }}>
              <Input
                label="Delivered By"
                type="text"
                name="deliveredBy"
                // value={formData.deliveredBy}
                onChange={(e) => handleInputChange("deliveredBy", e.target.value)}
                placeholder="Person who delivered"
                fullWidth
              />
            </div>
          </div>

          <div className={styles.summary_section}>
            <div className={styles.summary_item}>
              <span className={styles.summary_label}>Total {firstIsIndividual ? "Devices" : "Boxes"}:</span>
              <span className={styles.summary_value}>{selectedDevices.length}</span>
            </div>
          </div>

          <div className={styles.selected_items}>
            {selectedDevices.map((device) => (
              <CollapsibleSection
                key={device.id}
                title={
                  firstIsIndividual
                    ? `Device ${device.serialImei} - ${device.type}`
                    : `Box No ${device.serialRegNo} - ${device.type}`
                }
                isOpen={!collapsedItems[device.id]}
                onClose={() => setCollapsedItems((prev) => ({ ...prev, [device.id]: !prev[device.id] }))}
                bodyHeight="auto"
                actions={[
                  <button
                    key="remove"
                    className={styles.remove_button}
                    onClick={() => handleRemoveDevice(device.id)}
                    title={`Remove ${firstIsIndividual ? "Device" : "Box"}`}
                    type="button"
                  >
                    ×
                  </button>,
                ]}
                content={
                  <div className={styles.card_content}>
                    <div className={styles.card_row}>
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>Case Manager</span>
                        <span className={styles.field_value}>{device.caseManager}</span>
                      </div>
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>Type</span>
                        <span className={styles.field_value}>{device.type}</span>
                      </div>
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>{firstIsIndividual ? "Serial No" : "Box No"}</span>
                        <span className={styles.field_value}>
                          {firstIsIndividual ? device.serialImei : device.serialRegNo}
                        </span>
                      </div>
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>Date</span>
                        <span className={styles.field_value}>{device.date}</span>
                      </div>
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>Status</span>
                        <span className={styles.field_value}>{device.status}</span>
                      </div>
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>Status</span>
                        <span className={styles.field_value}>{device.status}</span>
                      </div>
                    </div>
                  </div>
                }
              />
            ))}
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default MoveDeviceForm
